/**
 * Speech Service using Web Speech API (100% free, works in browser)
 * Provides Speech-to-Text (STT) and Text-to-Speech (TTS) functionality
 */

declare global {
  interface Window {
    SpeechRecognition?: new () => unknown;
    webkitSpeechRecognition?: new () => unknown;
  }
}

/** Web Speech API: result event structure */
interface SpeechRecognitionResultEvent {
  results: { length: number; [i: number]: { isFinal: boolean; 0: { transcript: string; confidence?: number } } };
  resultIndex: number;
}

/** Web Speech API: recognition instance (subset we use) */
interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  lang: string;
  start: () => void;
  stop: () => void;
  onresult: ((event: SpeechRecognitionResultEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
  onaudiostart: (() => void) | null;
  onspeechstart: (() => void) | null;
  onspeechend: (() => void) | null;
  onaudioend: (() => void) | null;
  onnomatch: (() => void) | null;
}

export interface VoiceConfig {
  rate?: number; // 0.1 to 10, default 1
  pitch?: number; // 0 to 2, default 1
  volume?: number; // 0 to 1, default 1
  lang?: string; // e.g., 'es-MX', 'es-ES'
}

export interface RecognitionResult {
  transcript: string;
  isFinal: boolean;
  confidence: number;
}

type RecognitionCallback = (result: RecognitionResult) => void;
type RecognitionErrorCallback = (error: string) => void;

class SpeechService {
  private synthesis: SpeechSynthesis | null = null;
  private recognition: SpeechRecognitionInstance | null = null;
  // @ts-expect-error - Keep reference to prevent GC
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private isRecognizing = false;

  // State for continuous listening with auto-restart
  private shouldContinueListening = false;
  private currentCallback: RecognitionCallback | null = null;
  private currentErrorCallback: RecognitionErrorCallback | null = null;
  // @ts-expect-error - Used for state tracking
  private currentLang = "es-MX";
  private lastInterimTranscript = "";

  constructor() {
    if (typeof window !== "undefined") {
      this.synthesis = window.speechSynthesis;

      // Initialize speech recognition (webkit prefix for Chrome)
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition() as SpeechRecognitionInstance;
        recognition.continuous = false; // Changed to false for better reliability
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;
        recognition.lang = "es-MX";
        this.recognition = recognition;
      }
    }
  }

  /**
   * Check if Text-to-Speech is supported
   */
  isTTSSupported(): boolean {
    return this.synthesis !== null && "speechSynthesis" in window;
  }

  /**
   * Check if Speech-to-Text is supported
   */
  isSTTSupported(): boolean {
    return this.recognition !== null;
  }

  /**
   * Get available voices for the specified language
   */
  async getVoices(lang: string = "es"): Promise<SpeechSynthesisVoice[]> {
    if (!this.synthesis) return [];

    return new Promise((resolve) => {
      let voices = this.synthesis!.getVoices();

      if (voices.length > 0) {
        resolve(voices.filter((v) => v.lang.startsWith(lang)));
      } else {
        // Wait for voices to load
        this.synthesis!.onvoiceschanged = () => {
          voices = this.synthesis!.getVoices();
          resolve(voices.filter((v) => v.lang.startsWith(lang)));
        };
      }
    });
  }

  /**
   * Detect if a name is likely female based on common Spanish name patterns
   */
  detectGenderFromName(name: string): "female" | "male" | "neutral" {
    if (!name) return "neutral";

    const normalizedName = name.toLowerCase().trim();
    const firstName = normalizedName.split(" ")[0]; // Get first name

    // Common Spanish female name endings
    const femaleEndings = [
      "a",
      "ia",
      "ina",
      "ela",
      "ela",
      "ana",
      "ena",
      "ina",
      "ona",
      "isa",
      "ita",
    ];
    // Common Spanish female names
    const femaleNames = [
      "maria",
      "ana",
      "carmen",
      "laura",
      "patricia",
      "guadalupe",
      "rosa",
      "martha",
      "monica",
      "sofia",
      "isabella",
      "valentina",
      "camila",
      "emilia",
      "elena",
      "andrea",
      "natalia",
      "diana",
      "paula",
      "fernanda",
      "alejandra",
      "claudia",
      "daniela",
      "gabriela",
      "juliana",
    ];

    // Common Spanish male name endings
    const maleEndings = [
      "o",
      "io",
      "eo",
      "an",
      "en",
      "in",
      "on",
      "el",
      "er",
      "or",
    ];
    // Common Spanish male names
    const maleNames = [
      "juan",
      "carlos",
      "jose",
      "luis",
      "miguel",
      "pedro",
      "antonio",
      "francisco",
      "diego",
      "santiago",
      "mateo",
      "sebastian",
      "nicolas",
      "alejandro",
      "daniel",
      "david",
      "gabriel",
      "adrian",
      "javier",
      "manuel",
      "ricardo",
      "fernando",
      "rodrigo",
      "pablo",
      "alberto",
    ];

    // Check exact name matches first
    if (femaleNames.includes(firstName)) return "female";
    if (maleNames.includes(firstName)) return "male";

    // Check endings
    for (const ending of femaleEndings) {
      if (firstName.endsWith(ending) && firstName.length > 3) {
        return "female";
      }
    }

    for (const ending of maleEndings) {
      if (firstName.endsWith(ending) && firstName.length > 3) {
        return "male";
      }
    }

    return "neutral";
  }

  /**
   * Select best voice for character based on name and preferences
   */
  async selectVoice(
    preferredLang: string = "es-MX",
    preferFemale?: boolean,
    characterName?: string,
  ): Promise<SpeechSynthesisVoice | null> {
    const voices = await this.getVoices("es");

    if (voices.length === 0) return null;

    // Determine gender preference from name if not explicitly provided
    let genderPreference: boolean | undefined = preferFemale;
    if (genderPreference === undefined && characterName) {
      const detectedGender = this.detectGenderFromName(characterName);
      genderPreference = detectedGender === "female";
    }

    // Try to find exact language match with gender preference
    let voice: SpeechSynthesisVoice | undefined;

    if (genderPreference !== undefined) {
      // Helper function to detect voice gender from voice name
      const isFemaleVoice = (voiceName: string): boolean => {
        const lower = voiceName.toLowerCase();
        return (
          lower.includes("female") ||
          lower.includes("mujer") ||
          lower.includes("woman") ||
          lower.includes("femenina") ||
          (lower.includes("español") &&
            (lower.includes("es-") || lower.includes("mexico")))
        );
      };

      const isMaleVoice = (voiceName: string): boolean => {
        const lower = voiceName.toLowerCase();
        return (
          lower.includes("male") ||
          lower.includes("hombre") ||
          lower.includes("man") ||
          lower.includes("masculina")
        );
      };

      // First try: exact language + gender
      voice = voices.find((v) => {
        const langMatch =
          v.lang === preferredLang ||
          v.lang.startsWith(preferredLang.split("-")[0]);
        const genderMatch = genderPreference
          ? isFemaleVoice(v.name)
          : isMaleVoice(v.name);
        return langMatch && genderMatch;
      });

      // Second try: any Spanish + gender (prioritize by pitch if available)
      if (!voice) {
        const candidates = voices.filter((v) => {
          const langMatch = v.lang.startsWith("es");
          const genderMatch = genderPreference
            ? isFemaleVoice(v.name)
            : isMaleVoice(v.name);
          return langMatch && genderMatch;
        });

        // If we have candidates, prefer voices with appropriate pitch characteristics
        // Female voices typically have higher default pitch
        if (candidates.length > 0) {
          voice = candidates.sort((a, b) => {
            // Prefer voices that match gender characteristics
            const aIsFemale = isFemaleVoice(a.name);
            const bIsFemale = isFemaleVoice(b.name);
            if (genderPreference) {
              return aIsFemale === bIsFemale ? 0 : aIsFemale ? -1 : 1;
            } else {
              return aIsFemale === bIsFemale ? 0 : aIsFemale ? 1 : -1;
            }
          })[0];
        }
      }

      // Third try: any Spanish voice (will adjust pitch later based on gender)
      if (!voice) {
        voice = voices.find((v) => v.lang.startsWith("es"));
      }
    }

    // Fourth try: exact language match (any gender)
    if (!voice) {
      voice = voices.find((v) => v.lang === preferredLang);
    }

    // Fallback to any Spanish voice
    if (!voice) {
      voice = voices.find((v) => v.lang.startsWith("es"));
    }

    return voice || voices[0];
  }

  /**
   * Analyze text to determine appropriate intonation
   */
  analyzeIntonation(text: string): { rate: number; pitch: number } {
    const normalizedText = text.toLowerCase().trim();

    // Question detection
    const isQuestion =
      normalizedText.includes("?") ||
      normalizedText.startsWith("¿") ||
      normalizedText.includes("pregunta") ||
      normalizedText.includes("qué") ||
      normalizedText.includes("cómo") ||
      normalizedText.includes("cuándo") ||
      normalizedText.includes("dónde") ||
      normalizedText.includes("por qué");

    // Exclamation detection
    const isExclamation =
      normalizedText.includes("!") ||
      normalizedText.startsWith("¡") ||
      normalizedText.includes("increíble") ||
      normalizedText.includes("excelente") ||
      normalizedText.includes("perfecto");

    // Statement/assertion detection
    const isAssertion =
      normalizedText.includes("debe") ||
      normalizedText.includes("tiene que") ||
      normalizedText.includes("es necesario") ||
      normalizedText.includes("según") ||
      normalizedText.includes("establece");

    // Base configuration
    let rate = 0.95; // Slightly slower for clarity
    let pitch = 1.0; // Neutral pitch

    // Adjust based on content type
    if (isQuestion) {
      rate = 0.9; // Slower for questions
      pitch = 1.15; // Higher pitch for questions (more natural)
    } else if (isExclamation) {
      rate = 1.0; // Normal speed
      pitch = 1.2; // Higher pitch for excitement
    } else if (isAssertion) {
      rate = 0.85; // Slower, more deliberate
      pitch = 0.95; // Slightly lower, more authoritative
    }

    // Adjust for text length (longer texts should be slightly faster)
    const wordCount = text.split(/\s+/).length;
    if (wordCount > 30) {
      rate = Math.min(rate * 1.05, 1.0);
    }

    return { rate, pitch };
  }

  /**
   * Speak text using Text-to-Speech with improved intonation
   */
  async speak(
    text: string,
    config: VoiceConfig = {},
    onEnd?: () => void,
    onError?: (error: Event) => void,
    characterName?: string,
  ): Promise<void> {
    if (!this.synthesis) {
      throw new Error("Text-to-Speech not supported in this browser");
    }

    // Stop any ongoing speech
    this.stopSpeaking();

    // Analyze text for better intonation
    const intonation = this.analyzeIntonation(text);

    // Detect gender from name for voice selection and pitch adjustment
    const detectedGender = characterName
      ? this.detectGenderFromName(characterName)
      : "neutral";
    const genderPreference = detectedGender === "female";

    // Adjust pitch based on gender if not explicitly set
    let basePitch = config.pitch ?? intonation.pitch;
    if (config.pitch === undefined && detectedGender !== "neutral") {
      // Female voices: slightly higher pitch (1.1-1.2)
      // Male voices: slightly lower pitch (0.9-0.95)
      if (detectedGender === "female") {
        basePitch = Math.max(intonation.pitch, 1.1);
      } else {
        basePitch = Math.min(intonation.pitch, 0.95);
      }
    }

    // Merge config with intonation analysis (config takes precedence)
    const finalConfig: VoiceConfig = {
      rate: config.rate ?? intonation.rate,
      pitch: basePitch,
      volume: config.volume ?? 1.0,
      lang: config.lang ?? "es-MX",
    };

    return new Promise((resolve, reject) => {
      const utterance = new SpeechSynthesisUtterance(text);

      // Configure voice with improved settings
      utterance.rate = finalConfig.rate!;
      utterance.pitch = finalConfig.pitch!;
      utterance.volume = finalConfig.volume ?? 1.0;
      utterance.lang = finalConfig.lang!;

      // Try to set a specific voice based on character gender
      this.selectVoice(finalConfig.lang, genderPreference, characterName).then(
        (voice) => {
          if (voice) {
            utterance.voice = voice;
            // If we couldn't find a gender-specific voice, adjust pitch more
            const voiceName = voice.name.toLowerCase();
            const hasGenderInName =
              voiceName.includes("female") ||
              voiceName.includes("male") ||
              voiceName.includes("mujer") ||
              voiceName.includes("hombre");

            if (!hasGenderInName && detectedGender !== "neutral") {
              // Further adjust pitch since voice doesn't have gender indicator
              if (detectedGender === "female") {
                utterance.pitch = Math.min(finalConfig.pitch! * 1.1, 2.0);
              } else {
                utterance.pitch = Math.max(finalConfig.pitch! * 0.9, 0.5);
              }
            }
          }

          utterance.onend = () => {
            this.currentUtterance = null;
            if (onEnd) onEnd();
            resolve();
          };

          utterance.onerror = (event: Event & { error?: string }) => {
            this.currentUtterance = null;

            // "interrupted" is normal when user wants to speak
            if (event.error === "interrupted") {
              resolve(); // Resolve instead of reject
              return;
            }

            if (onError) onError(event);
            reject(new Error("Speech synthesis error"));
          };

          this.currentUtterance = utterance;
          this.synthesis!.speak(utterance);
        },
      );
    });
  }

  /**
   * Stop current speech
   */
  stopSpeaking(): void {
    if (this.synthesis && this.synthesis.speaking) {
      this.synthesis.cancel();
      this.currentUtterance = null;
    }
  }

  /**
   * Pause current speech
   */
  pauseSpeaking(): void {
    if (this.synthesis && this.synthesis.speaking && !this.synthesis.paused) {
      this.synthesis.pause();
    }
  }

  /**
   * Resume paused speech
   */
  resumeSpeaking(): void {
    if (this.synthesis && this.synthesis.paused) {
      this.synthesis.resume();
    }
  }

  /**
   * Check if currently speaking
   */
  isSpeaking(): boolean {
    return this.synthesis?.speaking || false;
  }

  /**
   * Start listening for speech input
   */
  startListening(
    onResult: RecognitionCallback,
    onError?: RecognitionErrorCallback,
    lang: string = "es-MX",
  ): void {
    if (!this.recognition) {
      if (onError) {
        onError("Speech recognition not supported in this browser");
      }
      return;
    }

    if (this.isRecognizing) {
      this.stopListening();
    }

    // Store callbacks and state for auto-restart
    this.currentCallback = onResult;
    this.currentErrorCallback = onError || null;
    this.currentLang = lang;
    this.shouldContinueListening = true;
    this.lastInterimTranscript = "";

    // CRITICAL: Set language FIRST
    this.recognition.lang = lang;

    // CRITICAL: Set ALL event listeners BEFORE calling start()

    // 1. Result handler - MOST IMPORTANT
    this.recognition.onresult = (event: SpeechRecognitionResultEvent) => {
      try {
        const results = event.results;

        if (!results || results.length === 0) {
          return;
        }

        // Process ALL results, not just the last one
        for (let i = event.resultIndex; i < results.length; i++) {
          const result = results[i];
          const transcript = result[0].transcript;
          const isFinal = result.isFinal;
          const confidence = result[0].confidence;

          if (transcript && transcript.trim()) {
            // Store last interim result for potential finalization
            if (!isFinal) {
              this.lastInterimTranscript = transcript;
            } else {
              // Clear interim when we get final
              this.lastInterimTranscript = "";
            }

            onResult({
              transcript: transcript,
              isFinal: isFinal,
              confidence: confidence || 0,
            });
          }
        }
      } catch (_e) {
        // Ignore recognition errors (handled by onerror)
        void _e;
      }
    };

    // 2. Error handler
    this.recognition.onerror = (event: { error: string }) => {
      const errorType = event.error;
      const benignErrors = ["no-speech", "aborted"];

      if (benignErrors.includes(errorType)) {
        return;
      }

      console.error("❌ Speech recognition error:", errorType);
      if (onError) {
        onError(errorType);
      }
    };

    // 3. End handler - WITH AUTO-RESTART
    this.recognition.onend = () => {
      this.isRecognizing = false;

      // Finalize any pending interim results before restart
      if (this.lastInterimTranscript && this.currentCallback) {
        this.currentCallback({
          transcript: this.lastInterimTranscript,
          isFinal: true,
          confidence: 0.8, // Reasonable confidence for finalized interim
        });
        this.lastInterimTranscript = "";
      }

      // Auto-restart if still in listening session
      if (this.shouldContinueListening) {
        // Small delay to prevent rapid restart issues
        setTimeout(() => {
          if (this.shouldContinueListening && this.recognition) {
            try {
              this.recognition.start();
            } catch (error) {
              console.error("❌ Error restarting recognition:", error);
              // If restart fails, stop the session
              this.shouldContinueListening = false;
              if (this.currentErrorCallback) {
                this.currentErrorCallback("Failed to restart recognition");
              }
            }
          }
        }, 100);
      }
    };

    // 4. Start handler
    this.recognition.onstart = () => {
      this.isRecognizing = true;
    };

    // 5. Audio start
    this.recognition.onaudiostart = () => {};

    // 6. Speech start
    this.recognition.onspeechstart = () => {};

    // 7. Speech end
    this.recognition.onspeechend = () => {};

    // 8. Audio end
    this.recognition.onaudioend = () => {};

    // 9. No match handler (important!)
    this.recognition.onnomatch = () => {};

    // NOW start recognition - all handlers are in place
    try {
      this.recognition.start();
    } catch (error) {
      console.error("❌ Error starting recognition:", error);
      if (onError) {
        onError("Failed to start listening");
      }
    }
  }

  /**
   * Stop listening for speech input
   */
  stopListening(): void {
    // Stop the auto-restart loop
    this.shouldContinueListening = false;
    this.lastInterimTranscript = "";

    if (this.recognition && this.isRecognizing) {
      try {
        this.recognition.stop();
        this.isRecognizing = false;
      } catch (error) {
        console.error("Error stopping recognition:", error);
      }
    }

    // Clear callbacks
    this.currentCallback = null;
    this.currentErrorCallback = null;
  }

  /**
   * Check if currently listening
   */
  isListening(): boolean {
    return this.isRecognizing;
  }

  /**
   * Request microphone permission
   */
  async requestMicrophonePermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop()); // Stop immediately, we just needed permission
      return true;
    } catch (error) {
      console.error("Microphone permission denied:", error);
      return false;
    }
  }
}

// Export singleton instance
export const speechService = new SpeechService();

// Character-specific voice configurations
export const CHARACTER_VOICES: Record<string, VoiceConfig> = {
  client: {
    rate: 1.0,
    pitch: 1.1,
    lang: "es-MX",
  },
  judge: {
    rate: 0.85,
    pitch: 0.9,
    lang: "es-ES",
  },
  opponent: {
    rate: 1.05,
    pitch: 1.0,
    lang: "es-MX",
  },
  mediator: {
    rate: 0.95,
    pitch: 1.0,
    lang: "es-ES",
  },
};
